<script>
  import { v4 as uuidv4 } from "uuid";
  import { gsap } from "gsap";
  import { onMount, tick } from "svelte";
  import { createEventDispatcher } from "svelte";

  const dispatch = createEventDispatcher();

  export let item1Url;
  export let item2Url;
  export let backgroundText;
  export let dark = false;

  const componentId = "carousel__" + uuidv4();
  let timelineItem1;
  let timelineItem2;

  const playAnimation = (dispatchEvent = true) => {
    if (dispatchEvent) {
      dispatch("carousel-click", { componentId, reverse: false });
    }
    timelineItem1.restart();
    timelineItem2.resume();

    handleClick = reverseAnimation;
  };

  const reverseAnimation = (dispatchEvent = true) => {
    if (dispatchEvent) {
      dispatch("carousel-click", { componentId, reverse: true });
    }
    timelineItem1.resume();
    timelineItem2.restart();

    handleClick = playAnimation;
  };

  let handleClick = playAnimation;

  const createTimeline = (itemNumber) => {
    const timeline = gsap.timeline({
      paused: true,
      defaults: {
        duration: 0.2,
      },
    });
    timeline
      .to("#" + componentId + " .carousel__item--" + itemNumber, {
        zIndex: 0,
        duration: 0,
        immediateRender: true,
      })
      .to("#" + componentId + " .carousel__item--" + itemNumber, {
        xPercent: (dark ? 1 : -1) * 36,
        transform: "scale(0.5)",
        onComplete: () => {
          timeline.pause();
        },
      })
      .to("#" + componentId + " .carousel__item--" + itemNumber, {
        zIndex: 1,
        duration: 0,
        immediateRender: true,
      })
      .to("#" + componentId + " .carousel__item--" + itemNumber, {
        xPercent: (dark ? -1 : 1) * 50,
        transform: "scale(1)",
      });
    return timeline;
  };

  export const carousel = {
    activateCarousel: () => {
      handleClick(false);
    },
  };

  onMount(() => {
    gsap.to("#" + componentId + " .carousel__background-text", {
      y: dark ? "-40%" : "40%",
      ease: "none",
      scrollTrigger: {
        trigger: "#" + componentId,
        start: "top 70%",
        end: "bottom 10%",
        scrub: true,
      },
    });

    timelineItem1 = createTimeline(1);
    timelineItem2 = createTimeline(2);

    setTimeout(() => {
      timelineItem2.play();
    }, 100);
  });
</script>

<div
  id={componentId}
  class="carousel no-select {$$props.class}"
  on:click={handleClick}
>
  <div class="carousel__background">
    <div
      class="carousel__background-text"
      class:carousel__background-text--dark={dark}
    >
      {backgroundText}
    </div>
    <div
      class="carousel__background-separator"
      class:carousel__background-separator--dark={dark}
    />
  </div>
  <img
    class="carousel__item carousel__item--1"
    src={item1Url}
    style="transform: translateX({dark ? '-' : ''}50%)"
  />
  <img
    class="carousel__item carousel__item--2"
    src={item2Url}
    style="transform: translateX({dark ? '-' : ''}50%)"
  />
</div>

<style lang="scss">
  @import "./assets/styles/variables";

  .carousel {
    height: 0;
    padding-top: 100%;
    position: relative;
    overflow: hidden;

    &:hover {
      cursor: pointer;
    }

    &__item {
      height: auto;
      filter: drop-shadow(0 0 16px rgba(0, 0, 0, 0.4));
      top: 0;
      width: 100%;

      &--1 {
        position: absolute;
      }

      &--2 {
        position: absolute;
      }
    }

    &__background {
      bottom: 0;
      left: 0;
      position: absolute;
      top: 0;
      right: 0;
      width: 100%;

      &-text {
        color: white;
        font-family: "Roboto Condensed", sans-serif;
        font-size: 288px;
        font-style: italic;
        font-weight: bold;
        line-height: 469px;
        position: relative;
        text-align: center;
        z-index: 0;

        @media screen and (min-width: $breakpoint-small) {
          font-size: 288px;
        }

        @media screen and (min-width: $breakpoint-medium) {
          font-size: 256px;
        }

        @media screen and (min-width: $breakpoint-large) {
          font-size: 400px;
        }

        &--dark {
          color: $color-orange-dark;
        }
      }

      &-separator {
        background-color: white;
        filter: brightness(1.5) drop-shadow(0 0 2rem rgba(0, 0, 0, 0.2));
        height: 1.4rem;
        left: 0;
        position: absolute;
        right: 0;
        top: 50%;
        transform-origin: right top;
        transform: translateY(-33.3%) translateX(-33.3%) rotate(-22.5deg);
        width: 150%;

        &--dark {
          background-color: $color-orange-light;
          filter: brightness(1.5) drop-shadow(0 0 2rem $color-orange-light);
          opacity: 0.9;
          transform: rotate(-22.5deg);
          transform-origin: left bottom;
        }
      }
    }
  }
</style>
